import React, { useState } from 'react';
import { useProjectCreationSettings, useProjectCreationCache } from '../../hooks/useProjectCreationSettings';
import ProjectCreationAnalyticsDashboard from './ProjectCreationAnalyticsDashboard';
import ErrorDisplay from '../ui/ErrorDisplay';
import { showToast } from '../../utils/toast';
import '../../styles/components/admin/ProjectCreationAnalyticsDashboard.css';
import '../../styles/components/admin/ProjectCreationSettingsTab.css';

interface ProjectCreationSettingsTabProps {}

interface LocalState {
  saving: boolean;
  success: string | null;
  activeTab: 'settings' | 'analytics';
  tempPointsValue: number;
  optimisticSettings: {
    pointsEnabled: boolean | null;
    linkedinEnabled: boolean | null;
  };
}

const ProjectCreationSettingsTab: React.FC<ProjectCreationSettingsTabProps> = () => {
  const { settings, loading, error, updateSetting } = useProjectCreationSettings();
  const { getCacheStats, invalidateAllCaches } = useProjectCreationCache();
  
  const [localState, setLocalState] = useState<LocalState>({
    saving: false,
    success: null,
    activeTab: 'settings',
    tempPointsValue: 0,
    optimisticSettings: {
      pointsEnabled: null,
      linkedinEnabled: null
    }
  });

  // Update temp value when settings change and clear optimistic updates
  React.useEffect(() => {
    if (settings?.minimum_points_required) {
      setLocalState(prev => ({ 
        ...prev, 
        tempPointsValue: settings.minimum_points_required.value,
        // Clear optimistic states when new data arrives
        optimisticSettings: {
          pointsEnabled: null,
          linkedinEnabled: null
        }
      }));
    }
  }, [settings]);

  // Clear success message after 3 seconds
  React.useEffect(() => {
    if (localState.success) {
      const timer = setTimeout(() => {
        setLocalState(prev => ({ ...prev, success: null }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [localState.success]);

  const updatePointsRequirement = async (enabled: boolean, value?: number) => {
    try {
      // Immediate UI update for better UX
      setLocalState(prev => ({ 
        ...prev, 
        saving: true, 
        success: null,
        optimisticSettings: {
          ...prev.optimisticSettings,
          pointsEnabled: enabled
        }
      }));
      
      const newValue = value !== undefined ? value : localState.tempPointsValue;
      
      // Validate points value
      if (enabled && (newValue < 0 || newValue > 10000 || !Number.isInteger(newValue))) {
        // Revert optimistic update on validation error
        setLocalState(prev => ({ 
          ...prev, 
          saving: false,
          optimisticSettings: {
            ...prev.optimisticSettings,
            pointsEnabled: null
          }
        }));
        throw new Error('Points value must be a whole number between 0 and 10,000');
      }

      await updateSetting('minimum_points_required', {
        enabled,
        value: newValue
      });

      // Force cache invalidation as an additional failsafe
      invalidateAllCaches();

      setLocalState(prev => ({
        ...prev,
        saving: false,
        success: 'Points requirement updated successfully',
        optimisticSettings: {
          ...prev.optimisticSettings,
          pointsEnabled: null // Clear optimistic state as real data is now available
        }
      }));
    } catch (error: any) {
      console.error('Error updating points requirement:', error);
      setLocalState(prev => ({
        ...prev,
        saving: false,
        success: null,
        optimisticSettings: {
          ...prev.optimisticSettings,
          pointsEnabled: null // Revert optimistic update on error
        }
      }));
    }
  };

  const updateLinkedInRequirement = async (enabled: boolean) => {
    try {
      // Immediate UI update for better UX
      setLocalState(prev => ({ 
        ...prev, 
        saving: true, 
        success: null,
        optimisticSettings: {
          ...prev.optimisticSettings,
          linkedinEnabled: enabled
        }
      }));
      
      await updateSetting('linkedin_auth_required', {
        enabled
      });

      // Force cache invalidation as an additional failsafe
      invalidateAllCaches();

      setLocalState(prev => ({
        ...prev,
        saving: false,
        success: 'LinkedIn requirement updated successfully',
        optimisticSettings: {
          ...prev.optimisticSettings,
          linkedinEnabled: null // Clear optimistic state as real data is now available
        }
      }));
    } catch (error: any) {
      console.error('Error updating LinkedIn requirement:', error);
      setLocalState(prev => ({
        ...prev,
        saving: false,
        success: null,
        optimisticSettings: {
          ...prev.optimisticSettings,
          linkedinEnabled: null // Revert optimistic update on error
        }
      }));
    }
  };

  const handlePointsValueChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    setLocalState(prev => ({ ...prev, tempPointsValue: numValue }));
  };

  const handlePointsValueBlur = () => {
    if (settings?.minimum_points_required.enabled) {
      updatePointsRequirement(true, localState.tempPointsValue);
    }
  };

  const clearMessages = () => {
    setLocalState(prev => ({ ...prev, success: null }));
  };

  // Helper functions to get current display values (considering optimistic updates)
  const getCurrentPointsEnabled = () => {
    return localState.optimisticSettings.pointsEnabled !== null 
      ? localState.optimisticSettings.pointsEnabled 
      : settings?.minimum_points_required.enabled || false;
  };

  const getCurrentLinkedInEnabled = () => {
    return localState.optimisticSettings.linkedinEnabled !== null 
      ? localState.optimisticSettings.linkedinEnabled 
      : settings?.linkedin_auth_required.enabled || false;
  };

  // Debug function to show cache stats
  const showCacheStats = () => {
    const stats = getCacheStats();
    console.log('Cache Stats:', stats);
    showToast(`Cache contains ${stats.size} entries: ${stats.keys.join(', ')}`, 'info');
  };

  const clearAllCaches = () => {
    invalidateAllCaches();
    setLocalState(prev => ({ ...prev, success: 'All caches cleared successfully' }));
  };

  if (loading) {
    return (
      <div className="project-creation-settings-tab" data-testid="project-creation-settings-tab">
        <div className="tab-header">
          <h3>Project Creation Settings</h3>
          <p className="text-muted">Configure access controls for project creation</p>
        </div>
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="project-creation-settings-tab" data-testid="project-creation-settings-tab">
        <div className="tab-header">
          <h3>Project Creation Settings</h3>
          <p className="text-muted">Configure access controls for project creation</p>
        </div>
        <div className="alert alert-warning">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Unable to load settings. Please try refreshing the page.
        </div>
      </div>
    );
  }

  return (
    <div className="project-creation-settings-tab" data-testid="project-creation-settings-tab">
      <div className="tab-header">
        <h3>Project Creation Settings</h3>
        <p className="text-muted">Configure access controls for project creation</p>
        
        {/* Sub-tabs for Settings and Analytics */}
        <div className="sub-tabs mt-3">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${localState.activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setLocalState(prev => ({ ...prev, activeTab: 'settings' }))}
              >
                <i className="bi bi-gear-fill me-2"></i>
                Settings
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${localState.activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setLocalState(prev => ({ ...prev, activeTab: 'analytics' }))}
              >
                <i className="bi bi-graph-up me-2"></i>
                Analytics
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link"
                onClick={showCacheStats}
                title="Show cache statistics"
              >
                <i className="bi bi-speedometer2 me-2"></i>
                Cache Stats
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link text-warning"
                onClick={clearAllCaches}
                title="Clear all caches"
              >
                <i className="bi bi-trash me-2"></i>
                Clear Cache
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <ErrorDisplay
          error={error}
          onDismiss={clearMessages}
          compact={true}
        />
      )}

      {/* Success Alert */}
      {localState.success && (
        <div className="alert alert-success alert-dismissible">
          <i className="bi bi-check-circle me-2"></i>
          {localState.success}
          <button 
            type="button" 
            className="btn-close" 
            onClick={clearMessages}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Tab Content */}
      {localState.activeTab === 'settings' && (
        <div className="settings-content">
        {/* Points Requirement Section */}
        <div className="setting-section">
          <div className="setting-header">
            <div className="setting-title">
              <h4>
                <i className="bi bi-star-fill me-2 text-warning"></i>
                Points Requirement
              </h4>
              <p className="text-muted mb-0">
                Require users to have a minimum number of points before they can create projects
              </p>
            </div>
            <div className="setting-toggle">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pointsRequiredToggle"
                  checked={getCurrentPointsEnabled()}
                  onChange={(e) => updatePointsRequirement(e.target.checked)}
                  disabled={localState.saving}
                />
                <label className="form-check-label" htmlFor="pointsRequiredToggle">
                  {getCurrentPointsEnabled() ? 'Enabled' : 'Disabled'}
                </label>
              </div>
            </div>
          </div>

          {getCurrentPointsEnabled() && (
            <div className="setting-controls">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Minimum Points Required</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={localState.tempPointsValue}
                        onChange={(e) => handlePointsValueChange(e.target.value)}
                        onBlur={handlePointsValueBlur}
                        min="0"
                        max="10000"
                        step="1"
                        disabled={localState.saving}
                        placeholder="Enter minimum points"
                      />
                      <span className="input-group-text">points</span>
                    </div>
                    <small className="form-text text-muted">
                      Users must have at least this many points to create projects (0-10,000)
                    </small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="setting-preview">
                    <h6>Preview</h6>
                    <div className="preview-card">
                      <div className="preview-icon">
                        <i className="bi bi-lock-fill text-danger"></i>
                      </div>
                      <div className="preview-content">
                        <strong>Access Restricted</strong>
                        <p className="mb-0">
                          You need at least {localState.tempPointsValue} points to create a project.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* LinkedIn Requirement Section */}
        <div className="setting-section">
          <div className="setting-header">
            <div className="setting-title">
              <h4>
                <i className="bi bi-linkedin me-2 text-primary"></i>
                LinkedIn Authentication
              </h4>
              <p className="text-muted mb-0">
                Require users to have a verified LinkedIn account before they can create projects
              </p>
            </div>
            <div className="setting-toggle">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="linkedinRequiredToggle"
                  checked={getCurrentLinkedInEnabled()}
                  onChange={(e) => updateLinkedInRequirement(e.target.checked)}
                  disabled={localState.saving}
                />
                <label className="form-check-label" htmlFor="linkedinRequiredToggle">
                  {getCurrentLinkedInEnabled() ? 'Required' : 'Optional'}
                </label>
              </div>
            </div>
          </div>

          {getCurrentLinkedInEnabled() && (
            <div className="setting-controls">
              <div className="row">
                <div className="col-md-6">
                  <div className="setting-info">
                    <div className="info-card">
                      <h6>
                        <i className="bi bi-info-circle me-2"></i>
                        LinkedIn Verification
                      </h6>
                      <ul className="mb-0">
                        <li>Users must connect their LinkedIn account</li>
                        <li>Helps verify professional profiles</li>
                        <li>Reduces spam and low-quality projects</li>
                        <li>Users can connect LinkedIn in their profile settings</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="setting-preview">
                    <h6>Preview</h6>
                    <div className="preview-card">
                      <div className="preview-icon">
                        <i className="bi bi-linkedin text-primary"></i>
                      </div>
                      <div className="preview-content">
                        <strong>LinkedIn Required</strong>
                        <p className="mb-0">
                          Connect your LinkedIn account to create projects.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Combined Requirements Preview */}
        {(getCurrentPointsEnabled() || getCurrentLinkedInEnabled()) && (
          <div className="setting-section">
            <div className="setting-header">
              <div className="setting-title">
                <h4>
                  <i className="bi bi-shield-check me-2 text-success"></i>
                  Active Requirements Summary
                </h4>
                <p className="text-muted mb-0">
                  Current requirements that users must meet to create projects
                </p>
              </div>
            </div>
            <div className="requirements-summary">
              <div className="summary-card">
                <h6>Users must meet the following to create projects:</h6>
                <ul className="requirements-list">
                  {getCurrentPointsEnabled() && (
                    <li>
                      <i className="bi bi-star-fill text-warning me-2"></i>
                      Have at least <strong>{settings?.minimum_points_required.value || localState.tempPointsValue} points</strong>
                    </li>
                  )}
                  {getCurrentLinkedInEnabled() && (
                    <li>
                      <i className="bi bi-linkedin text-primary me-2"></i>
                      Have a connected <strong>LinkedIn account</strong>
                    </li>
                  )}
                </ul>
                {getCurrentPointsEnabled() && getCurrentLinkedInEnabled() && (
                  <div className="alert alert-info mt-3 mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Both requirements must be met</strong> - users need sufficient points AND LinkedIn authentication.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* No Requirements Active */}
        {!getCurrentPointsEnabled() && !getCurrentLinkedInEnabled() && (
          <div className="setting-section">
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>No restrictions active</strong> - All authenticated users can create projects.
            </div>
          </div>
        )}
        </div>
      )}

      {/* Analytics Tab Content */}
      {localState.activeTab === 'analytics' && (
        <div className="analytics-content">
          <ProjectCreationAnalyticsDashboard />
        </div>
      )}

      {/* Loading Overlay */}
      {localState.saving && (
        <div className="saving-overlay">
          <div className="saving-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Saving...</span>
            </div>
            <p className="mt-2 mb-0">Updating settings...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCreationSettingsTab;